import { FISH_ISLANDS, ageClasses, classAgeYears, classDay, classesOf, fishCounts, monthStep, stockFishing, type FishSpeciesId } from './fish';
import { eatenBy, type Predation } from './predation';
import { REEF_SPECIES } from './systems/fish';
import { HONU, HONUEA, classAgeYears as honuAgeYears, stagesOn, stepOneHonu, type TurtleLife } from './turtles';
import { MONK_SEAL, SEAL_DIVES_BELOW_30M, sealAgeYears, stepOneSeal } from './seals';
import { TIGER_SHARK, TIGER_SHARK_OFF_SHELF, TIGER_SHARK_TRIP_DAYS, offShelfSized, sharkAgeYears, stepOneShark } from './sharks';
import { MAUI_NUI_SHELF_BANDS_KM2 } from './data/seafloor/mauiNui';
import { HUMPBACK, inHawaiiShare, stageOf as whaleStageOf, stepOneWhale, whaleAgeYears, whaleInHawaii } from './whales';
import { HUMPBACK_SUPPLY } from './data/whales/humpback';
import { Rng } from './rng';
import { STREAM, mix32 } from './streams';
import { civilFromEpochDay, epochDayOf } from './calendar';
import type { SiteId } from './data/sites';
import type { World } from './types';
import { islandFishing } from './management';

/**
 * The animals and groups the player tags, tracks and names (M148, the user's word: "allow players to tag, track, and
 * name creatures (even if it is a group of animals)"; the user's choices: a tag follows a real life, each day its own
 * class's measured chances; the price shown, not charged until the economy exists; the tagged listed, marked in the
 * views and on the map; groups the schools and pods). A tagged animal is one of its population's, drawn from its counts
 * as it is tagged, and lives as its class does, its draws on a stream of its own, so tagging moves no other animal:
 * - a reef stock's (the fish and the octopus): each day its chance of dying the stock's own for its age class on its
 *   island (`classDay`, the predators' chances among it), a death's cause by the rates the death audit shares the
 *   stock's dead by; it keeps to its island's reef, as its stock does (the Slope's visitors, M147, are fish beyond
 *   the reef's stocks);
 * - a honu (`stepOneHonu`): its stage's chance at home or away, an adult leaving to breed at French Frigate Shoals and
 *   coming home as the population's breeders do;
 * - a monk seal (`stepOneSeal`) and a humpback (`stepOneWhale`): their age's or stage's chance, dead of age past their
 *   oldest class; a humpback in Hawaiʻi's waters on the days the population's share there is above its own place in it
 *   (`whaleInHawaii`), on its feeding grounds or the way there the rest;
 * - a hawksbill (`stepOneHonu`, its own life's) and a tiger shark (`stepOneShark`), M203's: the hawksbill as the honu
 *   is, an adult away over its breeding season as the population's breeders are; the shark its class's chance, dead of
 *   age past its oldest.
 * Each one's habitat today is its tag's to tell (`tagPlaceOn`, M203).
 */

/** The animals that can be tagged: the reef's stocks, the honu, the hawksbills, the monk seals, the tiger sharks (the last two M203's) and the humpbacks. */
export type TagSpecies = FishSpeciesId | 'honu' | 'honuea' | 'monkSeal' | 'tigerShark' | 'humpback';

/** The kinds tagged as a whole population at their sanctuary, not a reef stock's by island. */
const WHOLE_POPULATIONS: readonly TagSpecies[] = ['honu', 'honuea', 'monkSeal', 'tigerShark', 'humpback'];

/** Whether a kind is a reef stock's (by island) rather than a whole population's at its sanctuary. */
export const isReefSpecies = (s: TagSpecies): s is FishSpeciesId => !WHOLE_POPULATIONS.includes(s);

/** Whether a kind is a sea turtle's, its sex drawn as it is tagged and its adults away over their breeding season. */
const isTurtle = (s: TagSpecies): s is 'honu' | 'honuea' => s === 'honu' || s === 'honuea';

/** A sea turtle's life. */
const turtleLife = (s: 'honu' | 'honuea'): TurtleLife => (s === 'honu' ? HONU : HONUEA);

/** How a tagged animal died: its class's causes (`tallyDead`'s), or of its age where every one dies at its oldest. */
export type TagCause = 'natural' | 'fished' | 'eaten' | 'crowded' | 'old age';

export interface TaggedAnimal {
  id: number;
  name: string;
  species: TagSpecies;
  siteId: SiteId;
  /** Its island, `FISH_ISLANDS` order, a reef stock's; null for the others, their sanctuary's whole. */
  island: number | null;
  /** Its age class: a reef stock's months since settling, aged on each month's first (`ageFish`); the others' years, on the first of January. */
  cls: number;
  /** A sea turtle's sex (it decides when an adult breeds), null for the rest. */
  female: boolean | null;
  /** Away from its sanctuary: a honu breeding at French Frigate Shoals, a hawksbill over its breeding season, a humpback on its feeding grounds or the way there. */
  away: boolean;
  /** A humpback's own place in its population's season (`whaleInHawaii`), an even draw as it is tagged; null for the rest. */
  quantile: number | null;
  /** The day it was tagged, days since 1 January 1970. */
  taggedOn: number;
  /** Its death, or null while it lives. */
  died: { on: number; cause: TagCause } | null;
  /** The group it was tagged with, or null. */
  groupId: number | null;
  /**
   * The drawn animal it was tagged from, the drawing's own key for it (an octopus's, `ReefOctopus.key`), so its name
   * stays over that one after a reload (M203, M201's open item: the memory was the session's); null where none was
   * followed, or it is drawn by its kind alone.
   */
  seenAs: number | null;
  /** The habitat's scene it was tagged in, where it stays the rest of its tagging day (M203: one tagged on the Reef there, not gone to its day's draw at once); null where it was tagged elsewhere. */
  seenIn: TagHabitat | null;
}

/** The habitats a tag can be followed in, each drawn as a scene of its own (M203). */
export type TagHabitat = 'reef' | 'slope' | 'blue';
export const TAG_HABITATS: readonly TagHabitat[] = ['reef', 'slope', 'blue'];

/** A named group, keeping its name as its animals come and go: a school of a reef stock on an island, or a pod (a whale with her calf, a group of honu) of the animals tagged into it. */
export interface TaggedGroup {
  id: number;
  name: string;
  kind: 'school' | 'pod';
  species: TagSpecies;
  siteId: SiteId;
  /** A school's island; null for a pod. */
  island: number | null;
  taggedOn: number;
  /** The drawn school it was named from, the drawing's own seed for it, so its name stays over that school after a reload (M203); null where none was followed. */
  seenAs: number | null;
}

export interface Tags {
  animals: TaggedAnimal[];
  groups: TaggedGroup[];
}

/** No tag yet. */
export const emptyTags = (): Tags => ({ animals: [], groups: [] });

/** A tag's own stream for a day and a purpose: the world's seed, the tag's id and the day, apart from every other stream (`STREAM.tags`). */
function tagStream(world: World, id: number, epochDay: number, purpose: number): Rng {
  return new Rng(mix32(mix32(world.seed ^ STREAM.tags) ^ Math.imul(id, 0x9e3779b1) ^ Math.imul(epochDay + 1, 0x85ebca77) ^ purpose));
}

function stockOf(species: FishSpeciesId): (typeof REEF_SPECIES)[number] {
  const sp = REEF_SPECIES.find((s) => s.life.id === species);
  if (!sp) throw new Error(`no reef species ${species}`);
  return sp;
}

/** What a tagging asks: the animal's kind (and a reef stock's island), an adult or a young one (the sprite tapped), and its name (already the command's, `commands.ts`). */
export interface TagRequest {
  siteId: SiteId;
  species: TagSpecies;
  island?: number;
  stage: 'adult' | 'young';
  name: string;
  groupId?: number;
  /** A reef stock's young of this total length or more only, mm: the size the young followed are drawn at (`YOUNG_SEEN_MM`, M125); every young where none. */
  minLengthMm?: number;
  /** The drawn animal it was tagged from (`TaggedAnimal.seenAs`), or none. */
  seenAs?: number;
  /** The habitat's scene it was tagged in (`TaggedAnimal.seenIn`), or none. */
  seenIn?: TagHabitat;
  /**
   * A whole population's animal's age class where the drawing drew the one tapped from its counts (M203's review: a pup
   * tagged drawn again as a juvenile 84% of the time, MEASURED by the reviewer): that class, so the one tagged is the one
   * seen; refused where it holds none of the stage asked. None, its class drawn as M148's.
   */
  cls?: number;
}

/** A class drawn by its weight, `u` even in [0, 1); the last where rounding leaves a remainder. */
function drawClass(weights: readonly number[], u: number): number {
  let v = u * weights.reduce((a, w) => a + w, 0);
  for (let c = 0; c < weights.length; c++) {
    v -= weights[c] as number;
    if (v < 0) return c;
  }
  return weights.length - 1;
}

/**
 * Tags one of a population's animals today: its age class drawn from its counts, each class by its animals of the
 * stage asked (a reef stock's on the island asked, adults by their mature share; a honu's at home by its class's stage;
 * a seal's adults from the first pupping age, `firstPupYears`; a humpback's adults by its stage, and only one in
 * Hawaiʻi's waters, its place in the season drawn below the day's share there), on the tag's own stream. Returns the
 * tagged animal, or why it could not be tagged.
 */
export function tagAnimal(world: World, req: TagRequest): TaggedAnimal | string {
  const sanctuary = world.sanctuaries.find((s) => s.siteId === req.siteId);
  if (!sanctuary) return 'no such sanctuary';
  const epochDay = epochDayOf(world);
  const adult = req.stage === 'adult';
  let weights: number[];
  let island: number | null = null;
  if (isReefSpecies(req.species)) {
    const counts = req.island === undefined ? undefined : sanctuary.fish?.[req.species]?.islands[req.island];
    if (!sanctuary.fish) return 'no such stock here';
    if (!counts) return 'no such island';
    island = req.island as number;
    const life = stockOf(req.species).life;
    const step = monthStep(epochDay);
    weights = counts.map((n, c) => {
      const k = classesOf(life)[step]?.[c];
      if (!k) return 0;
      // The young by the share `youthOf` draws as young (review M148: by the share not mature, recruits of 2 cm counted).
      return n * (adult ? k.adult + k.terminal : k.tlMm >= (req.minLengthMm ?? 0) ? k.young : 0);
    });
  } else if (isTurtle(req.species)) {
    if (!sanctuary.turtles) return req.species === 'honu' ? 'no honu here' : 'no hawksbills here';
    const stages = stagesOn(turtleLife(req.species), epochDay);
    weights = sanctuary.turtles[req.species].home.map((n, c) => ((stages[c] === 'adult') === adult ? n : 0));
  } else if (req.species === 'tigerShark') {
    if (!sanctuary.sharks) return 'no tiger sharks here';
    // An adult from female maturity, as the visitors are drawn (`sharkVisitor`).
    weights = sanctuary.sharks.tiger.counts.map((n, c) => ((sharkAgeYears(TIGER_SHARK, c, epochDay) >= TIGER_SHARK.femaleMatureYears) === adult ? n : 0));
  } else if (req.species === 'monkSeal') {
    if (!sanctuary.seals) return 'no monk seals here';
    // An adult from class `firstPupYears`, as the population pups and its sprites are drawn (`sealStages`; review M148: by
    // its age to the day, a class younger from January to its birthday).
    weights = sanctuary.seals.monk.counts.map((n, c) => ((c >= MONK_SEAL.firstPupYears) === adult ? n : 0));
  } else {
    if (!sanctuary.whales) return 'no humpbacks here';
    if (!(inHawaiiShare(HUMPBACK, epochDay) > 0)) return 'none in Hawaiʻi\'s waters today';
    weights = sanctuary.whales.humpback.counts.map((n, c) => ((whaleStageOf(HUMPBACK, whaleAgeYears(HUMPBACK, c, epochDay)) === 'adult') === adult ? n : 0));
  }
  if (!(weights.reduce((a, w) => a + w, 0) > 0)) return `none of its ${req.stage} here`;
  if (req.cls !== undefined && (isReefSpecies(req.species) || !((weights[req.cls] ?? 0) > 0))) return `none of its ${req.stage} of that age here`;
  const id = world.nextEntityId++;
  const rng = tagStream(world, id, epochDay, 0);
  const drawn = drawClass(weights, rng.next());
  const cls = req.cls ?? drawn;
  const female = isTurtle(req.species) ? rng.next() < turtleLife(req.species).femaleShare : null;
  const quantile = req.species === 'humpback' ? rng.next() * inHawaiiShare(HUMPBACK, epochDay) : null;
  const animal: TaggedAnimal = { id, name: req.name, species: req.species, siteId: req.siteId, island, cls, female, away: false, quantile, taggedOn: epochDay, died: null, groupId: req.groupId ?? null, seenAs: req.seenAs ?? null, seenIn: req.seenIn ?? null };
  world.tags.animals.push(animal);
  return animal;
}

/** Whether a sanctuary holds a kind's population (review M148: a pod of a kind not there saved a world its load refused). */
function holds(s: World['sanctuaries'][number], species: TagSpecies): boolean {
  if (isTurtle(species)) return Boolean(s.turtles);
  if (species === 'monkSeal') return Boolean(s.seals);
  if (species === 'tigerShark') return Boolean(s.sharks);
  if (species === 'humpback') return Boolean(s.whales);
  return Boolean(s.fish?.[species]);
}

/** Names a school of a reef stock on an island, or a pod (its animals tagged into it after), as a group. */
export function tagGroup(world: World, req: { siteId: SiteId; kind: 'school' | 'pod'; species: TagSpecies; island?: number; name: string; seenAs?: number }): TaggedGroup | string {
  const sanctuary = world.sanctuaries.find((s) => s.siteId === req.siteId);
  if (req.kind === 'school') {
    if (!isReefSpecies(req.species)) return 'a school is a reef stock\'s';
    if (req.island === undefined || !sanctuary?.fish?.[req.species]?.islands[req.island]) return 'no such stock here';
  } else if (!sanctuary) return 'no such sanctuary';
  else if (!holds(sanctuary, req.species)) return 'none of its kind here';
  const group: TaggedGroup = { id: world.nextEntityId++, name: req.name, kind: req.kind, species: req.species, siteId: req.siteId, island: req.kind === 'school' ? (req.island as number) : null, taggedOn: epochDayOf(world), seenAs: req.seenAs ?? null };
  world.tags.groups.push(group);
  return group;
}

/** Renames a tagged animal or group (the name already the command's); false where there is none of that id. */
export function renameTag(world: World, id: number, name: string): boolean {
  const t = world.tags.animals.find((a) => a.id === id) ?? world.tags.groups.find((g) => g.id === id);
  if (!t) return false;
  t.name = name;
  return true;
}

/**
 * A sanctuary's tagged reef animals' day, its stocks aged and the day's hunts reckoned (`stepReef`'s `onHunts`): each
 * living one a class older on a month's first, as its stock's, then dead or not by its class's chance, and its cause by
 * the class's rates. Its draws its own: no other stream moves.
 */
export function stepTaggedFish(world: World, siteId: SiteId, epochDay: number, hunts: readonly Predation[]): void {
  const sanctuary = world.sanctuaries.find((s) => s.siteId === siteId);
  if (!sanctuary?.fish) return;
  const firstOfMonth = civilFromEpochDay(epochDay).day === 1;
  for (const a of world.tags.animals) {
    if (a.died || a.siteId !== siteId || !isReefSpecies(a.species) || a.island === null) continue;
    const sp = stockOf(a.species);
    if (firstOfMonth) a.cls = Math.min(a.cls + 1, ageClasses(sp.life) - 1);
    const counts = sanctuary.fish[a.species].islands[a.island] as readonly number[];
    const eaten = eatenBy(hunts, a.species)?.chances[a.island]?.[a.cls] ?? 0;
    // Its island's fishing, lower where the sanctuary protects its stock (M165).
    const fishing = islandFishing(sp, sanctuary.management, epochDay)?.[a.island] ?? stockFishing(sp.supply);
    const d = classDay(sp.life, fishing, counts, a.island, a.cls, epochDay, eaten);
    const rng = tagStream(world, a.id, epochDay, 1);
    if (rng.next() >= d.chance) continue;
    if (d.old) {
      a.died = { on: epochDay, cause: 'old age' };
      continue;
    }
    const r = d.rates;
    let v = rng.next() * (r.natural + r.fished + r.eaten + r.crowded);
    const cause: TagCause = (v -= r.fished) < 0 ? 'fished' : (v -= r.eaten) < 0 ? 'eaten' : (v -= r.crowded) < 0 ? 'crowded' : 'natural';
    a.died = { on: epochDay, cause };
  }
}

/** The tagged sea turtles', seals', sharks' and humpbacks' day (the tick's, after their populations'): each living one's, on its own stream. */
export function stepTaggedOthers(world: World, epochDay: number): void {
  for (const a of world.tags.animals) {
    if (a.died || isReefSpecies(a.species)) continue;
    const rng = tagStream(world, a.id, epochDay, 1);
    const u = (): number => rng.next();
    if (isTurtle(a.species)) {
      const h = { cls: a.cls, female: a.female ?? true, away: a.away };
      const dead = stepOneHonu(turtleLife(a.species), h, epochDay, u);
      a.cls = h.cls;
      a.away = h.away;
      if (dead) a.died = { on: epochDay, cause: 'natural' };
    } else {
      const one = { cls: a.cls };
      const cause = a.species === 'monkSeal' ? stepOneSeal(MONK_SEAL, one, epochDay, u) : a.species === 'tigerShark' ? stepOneShark(TIGER_SHARK, one, epochDay, u) : stepOneWhale(HUMPBACK, HUMPBACK_SUPPLY, one, epochDay, u);
      a.cls = one.cls;
      if (a.species === 'humpback') a.away = !whaleInHawaii(HUMPBACK, a.quantile ?? 0, epochDay);
      if (cause) a.died = { on: epochDay, cause };
    }
  }
}

/**
 * Where a tagged animal is on a day among its sanctuary's habitats (M203, the user's word: "what if i made it so that the
 * ones that are uncommon or rarely seen instead are trackable when they leave a certain biome into different ones? ...
 * i feel like otherwise its too rare to see certain ones"), as its tag tells it: one habitat a day (JUDGED: a satellite
 * tag's few fixes a day and a receiver's detections place an animal by the day, and the scenes hold a day's visitors),
 * drawn on its tag's own stream (`tagStream`, apart from its life's draws) by its kind's time where it is measured:
 * - a reef stock's fish or octopus: its island's reef, as its stock keeps to it;
 * - a honu or a hawksbill at home: the Reef ("Dives deeper than 14 meters were uncommon" of the honu, `HONU_DAY`; no
 *   share of a Hawaiian hawksbill's time off the reef read, M187);
 * - a monk seal: the Slope on the share of its dives below 30 m (`SEAL_DIVES_BELOW_30M`), the Reef the rest, a
 *   dive's share taken as a day's (a reading);
 * - a tiger shark: off the shelf in the Blue Water on its trips there (M211), each `TIGER_SHARK_TRIP_DAYS` long, one
 *   made in a block of that many days on the share of its time off it (`TIGER_SHARK_OFF_SHELF`), drawn once a block,
 *   from the sizes the share was tracked on (`offShelfSized`; the smaller kept on the shelf), the block's days its own
 *   but for the day it was tagged in a habitat seen (`seenIn`);
 *   on the shelf the rest, the Reef or the Slope by each one's area of Maui Nui's shelf (`MAUI_NUI_SHELF_BANDS_KM2`:
 *   518.6 and 3,146.4 km², MEASURED from GEBCO; the shelf's floor below 150 m the Slope's since M215, a place of its
 *   own with no scene before), as its visits are drawn at one density over the shelf (no share of its time by depth
 *   measured; the Slope's 85.8% agrees with "the modal bathymetry depth, and likely centre of tiger shark activity, is
 *   between 50 and 100 m", Meyer et al. 2018, p. 2);
 * - a humpback in Hawaiʻi's waters: the Blue Water, where the game draws its whales;
 * and away (a turtle breeding, a humpback on its feeding grounds); null once dead. On the day it is tagged, the habitat
 * it was tagged in, where one was (`seenIn`).
 */
export type TagPlace = 'reef' | 'slope' | 'blue' | 'away';

export function tagPlaceOn(world: World, a: TaggedAnimal, epochDay: number): TagPlace | null {
  if (a.died && a.died.on <= epochDay) return null;
  if (a.away) return 'away';
  if (epochDay === a.taggedOn && a.seenIn !== null) return a.seenIn;
  if (isReefSpecies(a.species) || isTurtle(a.species)) return 'reef';
  if (a.species === 'humpback') return 'blue';
  const u = tagStream(world, a.id, epochDay, 2).next();
  if (a.species === 'monkSeal') return u < SEAL_DIVES_BELOW_30M ? 'slope' : 'reef';
  // Its trip's block's own draw (purpose 3, by the block's first day), so a trip holds for its days; a young shark
  // sized for one only from a block it starts sized (M211's second review: one growing past the least size partway
  // through a block took the block's last days off the shelf, a trip a day long).
  const trip = TIGER_SHARK_TRIP_DAYS * Math.floor(epochDay / TIGER_SHARK_TRIP_DAYS);
  if (offShelfSized(TIGER_SHARK, a.cls, trip) && offShelfSized(TIGER_SHARK, a.cls, epochDay) && tagStream(world, a.id, trip, 3).next() < TIGER_SHARK_OFF_SHELF) return 'blue';
  const b = MAUI_NUI_SHELF_BANDS_KM2;
  return u * (b.reef + b.slope) < b.reef ? 'reef' : 'slope';
}

/** A tagged animal's age in years today (or at its death). */
function tagAgeYears(a: TaggedAnimal, epochDay: number): number {
  const day = a.died?.on ?? epochDay;
  if (isReefSpecies(a.species)) return classAgeYears(stockOf(a.species).life, a.cls, monthStep(day));
  if (isTurtle(a.species)) return honuAgeYears(turtleLife(a.species), a.cls, day);
  if (a.species === 'tigerShark') return sharkAgeYears(TIGER_SHARK, a.cls, day);
  return a.species === 'monkSeal' ? sealAgeYears(MONK_SEAL, a.cls, day) : whaleAgeYears(HUMPBACK, a.cls, day);
}

/** Whether any living tagged reef animal is at a site (the tick reckons their days only then). */
export const livingTagsAt = (world: World, siteId: SiteId): boolean => world.tags.animals.some((a) => !a.died && a.siteId === siteId && isReefSpecies(a.species));

/**
 * A tag's price, shown and not yet charged (the user's word, M148: "Price shown, charged later"; the economy's research
 * fund, $250,000, when it is built). Each animal's tag the kind its studies use:
 * - an acoustic transmitter for the reef's fish and the octopus (Meyer's tracking of the ulua, the kala and the roi; Ivey
 *   2007's of Octopus cyanea): its price not yet read, the figures found (Innovasea's quotes in the University of Southern
 *   Mississippi's sole-source notices) on a host this environment refuses, asked of the user;
 * - a satellite tag for the honu and the hawksbill: "satellite telemetry may cost up to US$ 5K per tracked animal"
 *   (Seminoff et al. 2012, PLoS ONE 7: e37403, p. 7, CC0; "up to US $5K per animal", p. 1), the tag, its data and their
 *   analysis together, an upper bound, of sea turtles;
 * - a satellite tag for the monk seal, the tiger shark (Meyer et al. 2018's at Maui, their fixes the floor's depths
 *   `wildlife.ts` tells) and the humpback: not yet read (the sources found on refused hosts).
 * PIT tags, "currently less than $2" (Šmejkal et al. 2020, PLoS ONE 15: e0229350, p. 8, CC BY), mark an animal for its
 * recapture, not its tracking, so none is sold here.
 */
export interface TagPrice {
  kind: string;
  usd: number | null;
  source: string;
}

export const TAG_PRICES: Readonly<Record<'acoustic' | 'turtleSatellite' | 'satellite', TagPrice>> = {
  acoustic: { kind: 'acoustic transmitter', usd: null, source: 'not yet read: the price lists found are on a host this game\'s builder could not reach' },
  turtleSatellite: { kind: 'satellite tag', usd: 5_000, source: 'Seminoff et al. 2012, PLoS ONE 7: e37403: "up to US $5K per animal", the tag, its data and their analysis' },
  satellite: { kind: 'satellite tag', usd: null, source: 'not yet read: the price lists found are on a host this game\'s builder could not reach' },
};

/** The tag a species is tagged with, and its price. */
const tagPriceOf = (species: TagSpecies): TagPrice => (isReefSpecies(species) ? TAG_PRICES.acoustic : isTurtle(species) ? TAG_PRICES.turtleSatellite : TAG_PRICES.satellite);

/** A tagged animal as the Tagged list tells it. */
export interface TagRecord {
  id: number;
  name: string;
  species: TagSpecies;
  siteId: SiteId;
  /** Where it is: a reef animal's island; the others at home or away. */
  where: { island: string } | { away: boolean };
  /** Its habitat today (`tagPlaceOn`, M203); null once dead. */
  place: TagPlace | null;
  /** Its age, years: today's, or at its death. */
  ageYears: number;
  taggedOn: number;
  /** Days followed: to today, or to its death. */
  daysTracked: number;
  died: TaggedAnimal['died'];
  group: string | null;
  price: TagPrice;
}

/** A named group as the list tells it: a school's island's grown fish of its stock today; a pod's living members. */
export interface GroupRecord {
  id: number;
  name: string;
  kind: TaggedGroup['kind'];
  species: TagSpecies;
  siteId: SiteId;
  island: string | null;
  taggedOn: number;
  /** A school's island's grown fish today; a pod's tagged members living. */
  count: number;
}

/** The world's tagged animals and groups for the list and the map, as of today; nothing written. */
export function tagRecords(world: World): { animals: TagRecord[]; groups: GroupRecord[] } {
  const today = epochDayOf(world);
  const groupName = new Map(world.tags.groups.map((g) => [g.id, g.name]));
  const animals = world.tags.animals.map((a): TagRecord => ({
    id: a.id, name: a.name, species: a.species, siteId: a.siteId, where: a.island === null ? { away: a.away } : { island: FISH_ISLANDS[a.island] as string }, place: tagPlaceOn(world, a, today),
    ageYears: tagAgeYears(a, today), taggedOn: a.taggedOn, daysTracked: (a.died?.on ?? today) - a.taggedOn, died: a.died,
    group: a.groupId === null ? null : groupName.get(a.groupId) ?? null, price: tagPriceOf(a.species),
  }));
  const groups = world.tags.groups.map((g): GroupRecord => {
    let count = world.tags.animals.filter((a) => a.groupId === g.id && !a.died).length;
    if (g.kind === 'school' && g.island !== null && isReefSpecies(g.species)) {
      const stock = world.sanctuaries.find((s) => s.siteId === g.siteId)?.fish?.[g.species];
      const own = stock ? { islands: stock.islands.map((c, i) => (i === g.island ? c : c.map(() => 0))), afloat: stock.afloat } : null;
      const c = own ? fishCounts(stockOf(g.species).life, own, today) : null;
      count = c ? c.adults + c.terminal : 0;
    }
    return { id: g.id, name: g.name, kind: g.kind, species: g.species, siteId: g.siteId, island: g.island === null ? null : (FISH_ISLANDS[g.island] as string), taggedOn: g.taggedOn, count };
  });
  return { animals, groups };
}
